import { useState } from 'react';
import { LuPlus, LuTag } from 'react-icons/lu';
import { TbDotsVertical } from 'react-icons/tb';

import { DBCategory } from '@core/types/types.db';
import { Badge } from '@frontend/components/ui/badge';
import { Button } from '@frontend/components/ui/button';
import { Card, CardContent } from '@frontend/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@frontend/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@frontend/components/ui/table';
import { CategoryCreateModal } from '@frontend/Settings/components/CategoryCreateModal';
import { CategoryDeleteDialog } from '@frontend/Settings/components/CategoryDeleteDialog';
import { CategoryEditModal } from '@frontend/Settings/components/CategoryEditModal';
import { exportCategoryMarkdown } from '@frontend/Settings/util/exportCategoryMarkdown';
import {
  useDirectoryCategories,
  useDirectoryTeam,
  useDirectoryUsers,
} from '@frontend/shared/hooks/useDirectory';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';

interface SettingsCategoriesProps {
  isReadOnly?: boolean;
}

export function SettingsCategories({
  isReadOnly = false,
}: SettingsCategoriesProps) {
  const team = useDirectoryTeam();
  const categories = useDirectoryCategories();
  const users = useDirectoryUsers();
  const [selectedCategory, setSelectedCategory] = useState<DBCategory | null>(
    null,
  );

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const exporter = useSubmitter(
    async (categoryId: string, categoryName: string) => {
      return exportCategoryMarkdown(categoryId, categoryName, users);
    },
  );

  const handleEdit = (category: DBCategory) => {
    setSelectedCategory(category);
    setEditModalOpen(true);
  };

  const handleDelete = (category: DBCategory) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  const handleSuccess = () => {
    setCreateModalOpen(false);
    setEditModalOpen(false);
    setDeleteDialogOpen(false);
    setSelectedCategory(null);
  };

  return (
    <>
      <Card className="mt-12 max-w-[600px]">
        <CardContent>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Categories</h2>
              <p className="mt-2 text-xs font-medium text-gray-500">
                Organize your questions into categories for better management
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => setCreateModalOpen(true)}
              disabled={isReadOnly}
            >
              <LuPlus className="mr-2 size-4" />
              Add Category
            </Button>
          </div>

          {!categories || categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <LuTag size={48} />
              <p className="mt-4 text-lg font-medium">No categories yet</p>
              <p className="max-w-[300px] text-center text-sm">
                Create your first category to start organizing your questions
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((category) => {
                  const isDefault = category.id === team.default_category_id;
                  return (
                    <TableRow key={category.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{category.name}</span>
                          {isDefault && (
                            <Badge variant="default">Default</Badge>
                          )}
                        </div>
                      </TableCell>

                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={isReadOnly}
                            >
                              <TbDotsVertical className="size-4" />
                              <span className="sr-only">Actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleEdit(category)}
                            >
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              variant="destructive"
                              disabled={isDefault}
                              onClick={() => handleDelete(category)}
                            >
                              Delete
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={exporter.isSubmitting}
                              onClick={() =>
                                exporter.submit(category.id, category.name)
                              }
                            >
                              {exporter.isSubmitting
                                ? 'Exporting...'
                                : 'Export Answers'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CategoryCreateModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleSuccess}
      />

      {selectedCategory && (
        <>
          <CategoryEditModal
            isOpen={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            category={selectedCategory}
            onSuccess={handleSuccess}
          />
          <CategoryDeleteDialog
            isOpen={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            category={selectedCategory}
            onSuccess={handleSuccess}
            isDefault={team.default_category_id === selectedCategory.id}
          />
        </>
      )}
    </>
  );
}
